import { useState } from "react";
import { KeyRound } from "lucide-react";
import { trpc } from "../../lib/trpc";
import { useLanguage } from "../../contexts/LanguageContext";
import { Button } from "../ui/button";
import { PasswordInput } from "../ui/password-input";
import { Label } from "../ui/label";
import { PasswordFields, newPasswordValid } from "./PasswordFields";
import { FormNotice } from "./FormNotice";

/** 變更密碼表單（/account 分頁與強制改密碼頁共用）。成功後呼叫 onSuccess。 */
export function ChangePasswordForm({ onSuccess, currentLabel }: { onSuccess?: () => void; currentLabel?: string }) {
  const { t } = useLanguage();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const m = trpc.account.changePassword.useMutation({
    onSuccess: () => {
      setDone(true);
      setCurrent("");
      setNext("");
      setConfirm("");
      onSuccess?.();
    },
    onError: (e) => setError(e.message),
  });

  const valid = current !== "" && newPasswordValid(next, confirm) && next !== current;

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        setDone(false);
        m.mutate({ currentPassword: current, newPassword: next });
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="pw-current" className="text-[12px]">
          {currentLabel ?? t({ "zh-TW": "目前密碼", "en-US": "Current password", "zh-CN": "目前密码" })}
        </Label>
        <PasswordInput
          id="pw-current"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
      </div>
      <PasswordFields password={next} confirm={confirm} onPassword={setNext} onConfirm={setConfirm} />
      {next !== "" && next === current && (
        <p className="text-[12px] text-destructive">
          {t({ "zh-TW": "新密碼不可與目前密碼相同。", "en-US": "New password must differ from the current one.", "zh-CN": "新密码不可与目前密码相同。" })}
        </p>
      )}
      {error && <FormNotice kind="error">{error}</FormNotice>}
      {done && (
        <FormNotice kind="success">
          {t({
            "zh-TW": "密碼已更新。其他裝置上的登入已失效，需重新登入。",
            "en-US": "Password updated. Sessions on other devices have been signed out.",
            "zh-CN": "密码已更新。其他装置上的登入已失效，需重新登入。",
          })}
        </FormNotice>
      )}
      <Button type="submit" disabled={!valid || m.isPending}>
        <KeyRound className="h-3.5 w-3.5" />
        {m.isPending
          ? t({ "zh-TW": "更新中…", "en-US": "Updating…", "zh-CN": "更新中…" })
          : t({ "zh-TW": "更新密碼", "en-US": "Update password", "zh-CN": "更新密码" })}
      </Button>
    </form>
  );
}
